import React, { useState, useRef } from "react";
import {
  DocumentDuplicateIcon,
  PencilIcon,
  ArchiveIcon,
  EyeIcon,
  HashtagIcon,
  CalendarIcon,
  ChartBarIcon,
  ClockIcon,
  GlobeAltIcon,
  SparklesIcon,
  BellIcon,
  DotsVerticalIcon,
  TrashIcon,
  ExclamationIcon,
  CodeIcon,
  UserIcon,
  ChatAlt2Icon,
} from "@heroicons/react/outline";
import { CheckCircleIcon, LightningBoltIcon } from "@heroicons/react/solid";

const TemplateCard = ({
  template,
  onEdit,
  onClone,
  onArchive,
  onPreview,
  onUnarchive = null,
  onDelete = null,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const cardRef = useRef(null);

  // Create a safe function to handle archive/unarchive
  const handleArchiveAction = (template) => {
    if (template.archived) {
      // Handle unarchive
      if (typeof onUnarchive === "function") {
        onUnarchive(template);
      } else if (typeof onArchive === "function") {
        // Fallback to using onArchive with a false parameter if onUnarchive isn't available
        onArchive(template, false);
      } else {
        console.warn(
          "Neither onUnarchive nor onArchive functions are available"
        );
      }
    } else {
      // Handle archive
      if (typeof onArchive === "function") {
        onArchive(template);
      } else {
        console.warn("onArchive function is not available");
      }
    }
  };

  // Create a safe function to handle delete
  const handleDeleteAction = (template, deleteAll = false) => {
    if (typeof onDelete === "function") {
      onDelete(template, deleteAll);
    } else {
      console.warn("onDelete function is not available");
    }
  };

  // Ensure template has required fields with proper defaults
  const safeTemplate = {
    key: template?.key || "UNKNOWN",
    version: template?.version || 1,
    locale: template?.locale || "en-IN",
    name: template?.name || template?.key || "Untitled Template",
    description:
      template?.description ||
      `Template for ${template?.category || "notifications"}`,
    archived: template?.archived || false,
    isPromotional: template?.isPromotional || false,
    category: template?.category || "TRANSACTIONAL",
    priority: template?.priority || "NORMAL",
    channels: template?.channels || ["FCM", "APNS", "WEB"],
    platforms: template?.platforms || [],
    tags: template?.tags || [],
    usageCount: template?.usageCount || 0,
    lastUsedAt: template?.lastUsedAt,
    updatedAt: template?.updatedAt || new Date().toISOString(),
    createdAt: template?.createdAt || new Date().toISOString(),
    titleTpl: template?.titleTpl || "",
    bodyTpl: template?.bodyTpl || "",
    variables: template?.variables || [],
    createdBy: template?.createdBy || "",
    updatedBy: template?.updatedBy || "",
    ...template,
  };

  // Format the platforms display
  const platformDisplay =
    safeTemplate.platforms.length > 0
      ? safeTemplate.platforms
      : safeTemplate.channels.map((ch) => {
          switch (ch) {
            case "FCM":
              return "Android";
            case "APNS":
              return "iOS";
            case "WEB":
              return "Web";
            case "WHATSAPP":
              return "WhatsApp";
            default:
              return ch;
          }
        });

  // Get category styles using theme colors
  const getCategoryStyles = (category) => {
    switch (category) {
      case "PROMOTIONAL":
        return {
          bg: "from-primary-light to-secondary",
          icon: "bg-primary-light/20 text-primary dark:bg-primary-dark/30 dark:text-primary-light",
          badge:
            "bg-gradient-to-r from-primary-light/20 to-secondary/20 text-primary border-primary-light/30 dark:from-primary-dark/30 dark:to-secondary-dark/30 dark:text-primary-light dark:border-primary-dark/50",
        };
      case "TRANSACTIONAL":
        return {
          bg: "from-secondary to-primary",
          icon: "bg-secondary/20 text-secondary dark:bg-secondary-dark/30 dark:text-secondary-light",
          badge:
            "bg-gradient-to-r from-secondary/20 to-primary/20 text-secondary border-secondary/30 dark:from-secondary-dark/30 dark:to-primary-dark/30 dark:text-secondary-light dark:border-secondary-dark/50",
        };
      case "REMINDER":
        return {
          bg: "from-warning to-warning-light",
          icon: "bg-warning/20 text-warning dark:bg-warning-dark/30 dark:text-warning-light",
          badge:
            "bg-gradient-to-r from-warning/20 to-warning-light/20 text-warning-dark border-warning/30 dark:from-warning-dark/30 dark:to-warning-light/20 dark:text-warning-light dark:border-warning-dark/50",
        };
      case "ALERT":
        return {
          bg: "from-danger to-danger-light",
          icon: "bg-danger/20 text-danger dark:bg-danger-dark/30 dark:text-danger-light",
          badge:
            "bg-gradient-to-r from-danger/20 to-danger-light/20 text-danger-dark border-danger/30 dark:from-danger-dark/30 dark:to-danger-light/20 dark:text-danger-light dark:border-danger-dark/50",
        };
      default:
        return {
          bg: "from-gray-500 to-slate-500",
          icon: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
          badge:
            "bg-gradient-to-r from-gray-50 to-slate-50 text-gray-700 border-gray-200 dark:from-gray-700 dark:to-slate-700 dark:text-gray-400 dark:border-gray-600",
        };
    }
  };

  const categoryStyles = getCategoryStyles(safeTemplate.category);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 1) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Get priority info with styling
  const getPriorityInfo = (priority) => {
    switch (priority) {
      case "HIGH":
        return {
          label: "High Priority",
          shortLabel: "High",
          classes:
            "bg-danger/20 text-danger-dark border-danger/30 dark:bg-danger-dark/30 dark:text-danger-light dark:border-danger-dark/50",
          icon: <LightningBoltIcon className="w-2.5 h-2.5" />,
        };
      case "URGENT":
        return {
          label: "Urgent",
          shortLabel: "Urgent",
          classes:
            "bg-danger/30 text-danger-dark border-danger/40 dark:bg-danger-dark/40 dark:text-danger-light dark:border-danger-dark/60",
          icon: <ExclamationIcon className="w-2.5 h-2.5" />,
        };
      case "LOW":
        return {
          label: "Low Priority",
          shortLabel: "Low",
          classes:
            "bg-neutral-light/20 text-gray-700 border-neutral-light/30 dark:bg-neutral-dark/20 dark:text-gray-300 dark:border-neutral-dark/30",
          icon: null,
        };
      default:
        return {
          label: "Normal",
          shortLabel: "Normal",
          classes:
            "bg-gray-100/50 text-gray-600 border-gray-200 dark:bg-gray-700/50 dark:text-gray-400 dark:border-gray-600",
          icon: null,
        };
    }
  };

  const priorityInfo = getPriorityInfo(safeTemplate.priority);

  // Calculate template age in days
  const templateAge = safeTemplate.createdAt
    ? Math.ceil(
        Math.abs(new Date() - new Date(safeTemplate.createdAt)) /
          (1000 * 60 * 60 * 24)
      )
    : 0;

  const actionsCount = safeTemplate.actions?.length || 0;

  // Get clean template body text (remove HTML tags)
  const getCleanTemplateText = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>?/gm, "").trim();
  };

  const templateTitle = safeTemplate.titleTpl || "No title";
  const templateBody =
    getCleanTemplateText(safeTemplate.bodyTpl) || "No body content";

  // Count variables if present
  const variableCount = safeTemplate.variables?.length || 0;

  // Format user ID to something more readable
  const formatUserId = (userId) => {
    if (!userId) return "Unknown";
    // If it's a mongo ObjectId, take the last 6 chars
    if (userId.length >= 24) {
      return `User ${userId.substring(userId.length - 6)}`;
    }
    return userId;
  };

  const createdByUser = formatUserId(safeTemplate.createdBy);
  const updatedByUser = formatUserId(safeTemplate.updatedBy);

  return (
    <div
      className="relative group h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowMoreActions(false);
      }}
      ref={cardRef}
    >
      {/* Card Container */}
      <div
        className={`
        relative bg-white dark:bg-gray-800 rounded-2xl h-full flex flex-col
        transition-all duration-300 transform
        ${isHovered ? "scale-[1.02] shadow-2xl" : "shadow-lg"}
        border border-gray-100 dark:border-gray-700
      `}
      >
        {/* Gradient Header Bar */}
        <div
          className={`h-1.5 bg-gradient-to-r ${categoryStyles.bg} rounded-t-2xl`}
        />

        {/* Main Content */}
        <div className="p-3 sm:p-4 lg:p-5 flex flex-col flex-grow">
          {/* Header Section with badge decorations */}
          <div className="flex justify-between items-start mb-2 sm:mb-3 gap-1 sm:gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-1 sm:gap-2">
                <div
                  className={`p-1 sm:p-1.5 rounded-lg ${categoryStyles.icon} transition-colors flex-shrink-0`}
                >
                  <BellIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 sm:gap-1.5">
                    <h3 className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white leading-tight truncate">
                      {safeTemplate.name}
                    </h3>

                    {/* Version badge */}
                    <span className="inline-flex items-center text-[9px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded-full bg-primary/10 text-primary dark:bg-primary-dark/30 dark:text-primary-light font-medium">
                      v{safeTemplate.version}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    <code className="text-[9px] sm:text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400 font-mono">
                      {safeTemplate.key}
                    </code>

                    {/* Show locale */}
                    <span className="flex items-center gap-1 text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400 px-1 sm:px-1.5 py-0.5 bg-gray-50 dark:bg-gray-700/50 rounded">
                      <GlobeAltIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      {safeTemplate.locale}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Badge - Improved with theme colors */}
            <div
              className={`
                px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-[10px] font-medium flex items-center gap-1 flex-shrink-0
                ${
                  safeTemplate.archived
                    ? "bg-neutral-light/30 text-gray-600 dark:bg-neutral-dark/30 dark:text-gray-400"
                    : "bg-gradient-to-r from-success/20 to-success-light/20 text-success-dark border border-success/30 dark:from-success-dark/30 dark:to-success-light/20 dark:text-success-light dark:border-success-dark/50"
                }
              `}
            >
              {!safeTemplate.archived && (
                <CheckCircleIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              )}
              {safeTemplate.archived ? "Archived" : "Active"}
            </div>
          </div>

          {/* Description - Always show with line clamp */}
          <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-300 mb-2 sm:mb-3 line-clamp-2 leading-relaxed">
            {safeTemplate.description}
          </p>

          {/* Template Content Preview - Always show */}
          <div className="mb-2 sm:mb-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-1.5 sm:p-2 border border-gray-100 dark:border-gray-600/50">
            {/* Title preview */}
            <div className="flex items-center gap-1 text-[9px] sm:text-[10px] text-gray-700 dark:text-gray-300 mb-1">
              <ChatAlt2Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary dark:text-primary-light" />
              <p className="font-medium line-clamp-1">{templateTitle}</p>
            </div>

            {/* Body preview */}
            <div className="text-[8px] sm:text-[9px] text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5 pl-3 sm:pl-4">
              {templateBody}
            </div>

            {/* Variables count if any */}
            {variableCount > 0 && (
              <div className="flex items-center gap-1 text-[8px] sm:text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1">
                <CodeIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                <span>
                  {variableCount}{" "}
                  {variableCount === 1 ? "variable" : "variables"}
                </span>
              </div>
            )}
          </div>

          {/* Category, Type, and Priority Badges */}
          <div className="flex flex-wrap items-center gap-1 sm:gap-1.5 mb-2 sm:mb-3">
            <span
              className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] sm:text-[10px] font-medium rounded-full border ${categoryStyles.badge}`}
            >
              {safeTemplate.category}
            </span>

            {/* Show promotional badge */}
            {safeTemplate.isPromotional && (
              <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] sm:text-[10px] font-medium rounded-full bg-gradient-to-r from-warning/20 to-warning-light/20 text-warning-dark border border-warning/30 dark:from-warning-dark/30 dark:to-warning-light/20 dark:text-warning-light dark:border-warning-dark/50 flex items-center gap-0.5">
                <SparklesIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                <span className="sm:inline">Promotional</span>
                <span className="inline sm:hidden">Promo</span>
              </span>
            )}

            {/* Show priority badge if not normal */}
            {safeTemplate.priority !== "NORMAL" && (
              <span
                className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] sm:text-[10px] font-medium rounded-full border ${priorityInfo.classes} flex items-center gap-0.5`}
              >
                {priorityInfo.icon}
                <span className="hidden sm:inline">{priorityInfo.label}</span>
                <span className="inline sm:hidden">
                  {priorityInfo.shortLabel}
                </span>
              </span>
            )}
          </div>

          {/* Platforms - Always show with responsive display */}
          <div className="flex flex-wrap gap-1 mb-2 sm:mb-3">
            {platformDisplay.map((platform, index) => (
              <span
                key={`${platform}-${index}`}
                className="px-1.5 sm:px-2 py-0.5 bg-gradient-to-r from-secondary/10 to-primary/10 dark:from-secondary-dark/20 dark:to-primary-dark/20 text-[9px] sm:text-[10px] font-medium rounded-md text-secondary-dark dark:text-secondary-light border border-secondary/20 dark:border-secondary-dark/30 truncate max-w-[60px] sm:max-w-none"
              >
                {platform}
              </span>
            ))}
          </div>

          {/* Tags - Always show with responsive display */}
          {safeTemplate.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2 sm:mb-3">
              {safeTemplate.tags.map((tag, index) => (
                <span
                  key={`${tag}-${index}`}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-primary/10 dark:bg-primary-dark/20 text-[9px] sm:text-[10px] font-medium rounded-md text-primary-dark dark:text-primary-light truncate max-w-[80px] sm:max-w-none"
                >
                  <HashtagIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Additional info section - Always show with responsive sizing */}
          <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-0.5 sm:gap-y-1 mb-2 sm:mb-3 mt-1 text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400">
            {/* Actions count */}
            {actionsCount > 0 && (
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-primary/20 dark:bg-primary-dark/30 flex items-center justify-center">
                  <span className="text-[7px] sm:text-[8px] font-bold text-primary-dark dark:text-primary-light">
                    {actionsCount}
                  </span>
                </span>
                <span>{actionsCount === 1 ? "Action" : "Actions"}</span>
              </div>
            )}

            {/* Created info with user */}
            <div className="flex items-center gap-1">
              <UserIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              <span>
                {createdByUser} · {formatDate(safeTemplate.createdAt)}
              </span>
            </div>

            {/* Template age */}
            {templateAge > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-gray-50 dark:bg-gray-700">
                {templateAge === 1 ? "1 day old" : `${templateAge} days old`}
              </span>
            )}
          </div>

          {/* Spacer to push stats to bottom */}
          <div className="flex-grow" />

          {/* Stats Section - Always show with responsive layout */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-2 mt-auto border-t border-gray-100 dark:border-gray-700 gap-1 sm:gap-2">
            <div className="flex items-center gap-2 sm:gap-3 text-[8px] sm:text-[10px] flex-wrap">
              {/* Usage stats */}
              <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                <ChartBarIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                <span className="font-medium">{safeTemplate.usageCount}</span>
                <span>{safeTemplate.usageCount === 1 ? "use" : "uses"}</span>
              </div>

              {/* Last updated info with user */}
              <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                <ClockIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                <span>
                  {updatedByUser !== createdByUser && `${updatedByUser} · `}
                  {formatDate(safeTemplate.updatedAt)}
                </span>
              </div>

              {/* Last used info */}
              {safeTemplate.lastUsedAt && (
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary/10 dark:bg-primary-dark/20 text-primary-dark dark:text-primary-light">
                  <span>Last used {formatDate(safeTemplate.lastUsedAt)}</span>
                </div>
              )}
            </div>

            {/* Action Buttons - Responsive buttons */}
            <div className="flex items-center gap-0.5 relative ml-auto mt-1 sm:mt-0">
              {/* Always show Preview and Edit */}
              <button
                onClick={() => onPreview(safeTemplate)}
                className="p-1 sm:p-1.5 rounded-lg text-gray-500 hover:text-primary hover:bg-primary/10 dark:hover:bg-primary-dark/20 transition-all duration-200"
                title="Preview"
              >
                <EyeIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </button>
              <button
                onClick={() => onEdit(safeTemplate)}
                disabled={safeTemplate.archived}
                className={`p-1 sm:p-1.5 rounded-lg transition-all duration-200 ${
                  safeTemplate.archived
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500 hover:text-success hover:bg-success/10 dark:hover:bg-success-dark/20"
                }`}
                title="Edit"
              >
                <PencilIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </button>

              {/* Action buttons for medium to large screens */}
              <div className="hidden sm:flex">
                <button
                  onClick={() => onClone(safeTemplate)}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-secondary hover:bg-secondary/10 dark:hover:bg-secondary-dark/20 transition-all duration-200"
                  title="Clone"
                >
                  <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleArchiveAction(safeTemplate)}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-warning hover:bg-warning/10 dark:hover:bg-warning-dark/20 transition-all duration-200"
                  title={safeTemplate.archived ? "Unarchive" : "Archive"}
                >
                  <ArchiveIcon className="w-3.5 h-3.5" />
                </button>
                {typeof onDelete === "function" && (
                  <button
                    onClick={() => handleDeleteAction(safeTemplate)}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-danger hover:bg-danger/10 dark:hover:bg-danger-dark/20 transition-all duration-200"
                    title="Delete"
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Show more menu for small screens */}
              <div className="sm:hidden">
                <button
                  onClick={() => setShowMoreActions(!showMoreActions)}
                  className="p-1 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                  title="More actions"
                >
                  <DotsVerticalIcon className="w-3 h-3" />
                </button>

                {/* Dropdown menu - enhanced with theme colors */}
                {showMoreActions && (
                  <div className="absolute right-0 bottom-full mb-1 w-28 sm:w-36 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-10">
                    <button
                      onClick={() => {
                        onClone(safeTemplate);
                        setShowMoreActions(false);
                      }}
                      className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs text-gray-700 dark:text-gray-300 hover:bg-secondary/10 dark:hover:bg-secondary-dark/20 flex items-center gap-1 sm:gap-2"
                    >
                      <DocumentDuplicateIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      Clone
                    </button>
                    <button
                      onClick={() => {
                        handleArchiveAction(safeTemplate);
                        setShowMoreActions(false);
                      }}
                      className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs text-gray-700 dark:text-gray-300 hover:bg-warning/10 dark:hover:bg-warning-dark/20 flex items-center gap-1 sm:gap-2"
                    >
                      <ArchiveIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      {safeTemplate.archived ? "Unarchive" : "Archive"}
                    </button>
                    {typeof onDelete === "function" && (
                      <button
                        onClick={() => {
                          handleDeleteAction(safeTemplate);
                          setShowMoreActions(false);
                        }}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs text-danger dark:text-danger-light hover:bg-danger/10 dark:hover:bg-danger-dark/20 flex items-center gap-1 sm:gap-2 rounded-b-lg"
                      >
                        <TrashIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hover Glow Effect - Using theme colors */}
      <div
        className={`
        absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none
        bg-gradient-to-r ${categoryStyles.bg} blur-xl -z-10 transform scale-95
      `}
        style={{ opacity: isHovered ? 0.15 : 0 }}
      />
    </div>
  );
};

export default TemplateCard;
